"use client";

import { useState, useRef, useEffect } from "react";
import { Sparkles, Brain, FileText, Palette, Wand2, Zap, X, ChevronDown } from "lucide-react";
import type { AiProfile } from "@/types/ai";
import { usePathname, useRouter } from "next/navigation";

interface GhostPill {
  id: string;
  text: string;
  icon: React.ReactNode;
  action: () => void;
  priority: number;
}

interface Props {
  profile: AiProfile;
  onSuggestionClick: (suggestion: string) => void;
  inputIconColor: string;
}

/**
 * Template Multi-Button
 * 
 * Combines template access with proactive Ghost Pills suggestions.
 * Clicking the Sparkles icon opens a popover with:
 * - Quick template access
 * - Contextual AI suggestions (Ghost Pills)
 */
export default function TemplateMultiButton({ profile, onSuggestionClick, inputIconColor }: Props) {
  const pathname = usePathname();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const primaryColor = profile.brand?.primaryColor || "#87CEEB";
  const secondaryColor = profile.brand?.secondaryColor || "#B0E0E6";

  // Generate contextual suggestions
  const [suggestions, setSuggestions] = useState<GhostPill[]>([]);

  useEffect(() => {
    const newSuggestions: GhostPill[] = [];

    // Builder page suggestions
    if (pathname?.startsWith("/builder")) {
      newSuggestions.push({
        id: "generate-icons",
        text: "Generate matching icons?",
        icon: <Sparkles className="w-3.5 h-3.5" />,
        action: () => {
          onSuggestionClick("Generate matching icons for my AI profile");
          setIsOpen(false);
        },
        priority: 1,
      });
      newSuggestions.push({
        id: "test-ai",
        text: "Test AI on this file?",
        icon: <Brain className="w-3.5 h-3.5" />,
        action: () => {
          onSuggestionClick("Test my AI with the current configuration");
          setIsOpen(false);
        },
        priority: 2,
      });
    }

    // Settings page suggestions
    if (pathname?.startsWith("/settings")) {
      newSuggestions.push({
        id: "optimize-colors",
        text: "Optimize color palette?",
        icon: <Palette className="w-3.5 h-3.5" />,
        action: () => {
          onSuggestionClick("Suggest an optimized color palette");
          setIsOpen(false);
        },
        priority: 1,
      });
    }

    // Chat page suggestions
    if (pathname?.startsWith("/chat")) {
      newSuggestions.push({
        id: "quick-action",
        text: "What can you help with?",
        icon: <Zap className="w-3.5 h-3.5" />,
        action: () => {
          onSuggestionClick("What can you help me with?");
          setIsOpen(false);
        },
        priority: 1,
      });
      newSuggestions.push({
        id: "explore-topics",
        text: "Explore related topics",
        icon: <FileText className="w-3.5 h-3.5" />,
        action: () => {
          onSuggestionClick("Show me related topics and ideas");
          setIsOpen(false);
        },
        priority: 2,
      });
    }

    // Sort by priority
    setSuggestions(newSuggestions.sort((a, b) => a.priority - b.priority).slice(0, 4));
  }, [pathname, onSuggestionClick]);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleTemplateClick = () => {
    router.push("/builder");
    setIsOpen(false);
  };

  return (
    <div className="relative flex-shrink-0" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`p-2 rounded-full transition-all duration-300 flex-shrink-0 relative ${
          isOpen ? "bg-white/30" : "hover:bg-white/20"
        }`}
        title="Templates & Suggestions"
        aria-label="Open templates and suggestions"
        style={{ color: inputIconColor }}
      >
        <Sparkles className="w-5 h-5" />
        {suggestions.length > 0 && (
          <span
            className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
            style={{ backgroundColor: primaryColor }}
            aria-hidden="true"
          />
        )}
      </button>

      {/* Popover */}
      {isOpen && (
        <div
          className="absolute bottom-full right-0 mb-2 w-64 rounded-2xl border shadow-xl z-50 animate-in fade-in slide-in-from-bottom-2"
          style={{
            background: `linear-gradient(135deg, ${primaryColor}08 0%, ${secondaryColor}08 100%)`,
            backdropFilter: "blur(24px) saturate(180%)",
            WebkitBackdropFilter: "blur(24px) saturate(180%)",
            borderColor: `${primaryColor}30`,
            boxShadow: `0 8px 32px ${primaryColor}25, inset 0 1px 0 rgba(255,255,255,0.1)`,
          }}
        >
          {/* Header */}
          <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: `${primaryColor}20` }}>
            <h3 className="text-sm font-semibold" style={{ color: profile.brand?.textColor || "#000000" }}>
              Templates & Suggestions
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded-full hover:bg-white/20 transition-colors"
              aria-label="Close"
            >
              <X className="w-3.5 h-3.5" style={{ color: profile.brand?.textColor || "#000000" }} />
            </button>
          </div>

          {/* Content */}
          <div className="p-2 max-h-80 overflow-y-auto">
            {/* Template Access */}
            <button
              onClick={handleTemplateClick}
              className="w-full p-3 rounded-xl mb-2 flex items-center gap-3 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: `linear-gradient(135deg, ${primaryColor}15, ${secondaryColor}15)`,
                border: `1px solid ${primaryColor}30`,
              }}
            >
              <Sparkles className="w-4 h-4" style={{ color: primaryColor }} />
              <div className="flex-1 text-left">
                <p className="text-sm font-medium" style={{ color: profile.brand?.textColor || "#000000" }}>
                  Browse Templates
                </p>
                <p className="text-xs opacity-70" style={{ color: profile.brand?.textSecondaryColor || "rgba(0,0,0,0.6)" }}>
                  Open builder to insert templates
                </p>
              </div>
              <ChevronDown className="w-4 h-4 rotate-[-90deg]" style={{ color: primaryColor }} />
            </button>

            {/* Ghost Pills Suggestions */}
            {suggestions.length > 0 && (
              <div>
                <p className="text-xs font-medium px-2 py-1 opacity-70" style={{ color: profile.brand?.textColor || "#000000" }}>
                  Quick Suggestions
                </p>
                <div className="space-y-1">
                  {suggestions.map((pill) => (
                    <button
                      key={pill.id}
                      onClick={pill.action}
                      className="w-full p-2.5 rounded-lg flex items-center gap-2.5 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] text-left"
                      style={{
                        background: `linear-gradient(135deg, ${primaryColor}10, ${secondaryColor}10)`,
                        border: `1px solid ${primaryColor}20`,
                      }}
                    >
                      <div style={{ color: primaryColor }}>{pill.icon}</div>
                      <span className="text-xs font-medium flex-1" style={{ color: profile.brand?.textColor || "#000000" }}>
                        {pill.text}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {suggestions.length === 0 && (
              <p className="text-xs text-center py-4 opacity-60" style={{ color: profile.brand?.textSecondaryColor || "rgba(0,0,0,0.6)" }}>
                No suggestions available
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
